import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Button, CircularProgress, Grid2, Stack, Typography } from '@mui/material';

// third-party
import OtpInput from 'react18-input-otp';

// project-imports
import AnimateButton from 'components/@extended/AnimateButton';

import AuthWrapper from 'sections/auth/AuthWrapper';
import { resendTwoFactorAuthenticationOtp } from 'services/auth.service';
import Snackbar from 'utils/Snackbar';

import useAuth from 'hooks/useAuth';
import { GetOrganizationDetailsByName } from 'services/organization.service';

const TwoFactorAuthentication = () => {
  const theme = useTheme();

  const { verifyTwoFactorAuthentication } = useAuth();
  const location = useLocation();
  const [otp, setOtp] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [authenticateWith, setAuthenticateWith] = useState<string>(location.state?.twoFactorAuthenticationWith);
  const [recoveryMode, setRecoveryMode] = useState<boolean>(false);

  const RESEND_TIME = 2 * 60 * 1000; // 2 minutes in milliseconds
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    // Initialize state from localStorage on component mount
    const storedTime = localStorage.getItem('otpTimer');
    if (storedTime) {
      const remainingTime = parseInt(storedTime, 10) - Date.now();
      return remainingTime > 0 ? remainingTime : 0;
    }
    return RESEND_TIME;
  });

  const borderColor = theme.palette.mode === 'dark' ? theme.palette.secondary[200] : theme.palette.secondary.light;

  const submitCodeForVerification = async (type?: string) => {
    verifyTwoFactorAuthentication(otp, location.state.userId, type);
  };

  const resetTimer = () => {
    const newExpireTime = Date.now() + RESEND_TIME;
    localStorage.setItem('otpTimer', newExpireTime.toString());
    setTimeLeft(RESEND_TIME);
  };

  const resetOtp = async () => {
    try {
      setIsLoading(true);
      const response = await resendTwoFactorAuthenticationOtp({ email: location.state.email });

      if (response.status === 200) {
        resetTimer(); // Reset timer only after successful API call
        Snackbar(response.data?.message, 'success');
      } else {
        Snackbar('Something went wrong.', 'error');
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      Snackbar(error.response?.data?.message || 'Something went wrong!', 'error');
    }
  };

  const formatTime = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleResend = () => {
    const newExpireTime = Date.now() + RESEND_TIME;
    localStorage.setItem('otpTimer', newExpireTime.toString());
    setTimeLeft(RESEND_TIME);
  };

  useEffect(() => {
    // Only start the timer if there's no existing timer
    if (!localStorage.getItem('otpTimer')) {
      handleResend();
    }

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = prev > 0 ? prev - 1000 : 0;
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (timeLeft === 0) {
      localStorage.removeItem('otpTimer');
    }
  }, [timeLeft]);

  const [myOrgData, setMyOrgData] = useState<any>({});

  useEffect(() => {
    if (!location?.state?.organization && window.location.host?.split('.' + process.env.REACT_APP_HOSTNAME)?.[0]) {
      GetOrganizationDetailsByName({ slug: window.location.host.split('.' + process.env.REACT_APP_HOSTNAME)[0] })
        .then((data: any) => {
          if (data.status === 200) {
            setMyOrgData(data.data.data);
          } else {
            Snackbar(data.data.message, 'error');
          }
        })
        .catch((err: any) => {
          Snackbar(err.message, 'error');
        });
    }
  }, []);

  return (
    <AuthWrapper organizationLogo={myOrgData?.basicsettings?.identity?.logo ?? {}}>
      <Grid2 container spacing={3}>
        <Grid2 size ={12}>
          <Stack spacing={1}>
            <Typography variant="h3">Two-Factor Authentication</Typography>
          </Stack>
        </Grid2>
        <Grid2 size ={12}>
          <Typography>
            {['email', 'mobileNo'].includes(authenticateWith) ? (
              <>We've sent an OTP to your registered {authenticateWith}.</>
            ) : (
              <>
                Please enter the displayed OTP from your {authenticateWith === 'microsoftAuth' ? 'Microsoft' : 'Google'} Authenticator app.
              </>
            )}
          </Typography>
        </Grid2>

        <Grid2 size ={12}>
          <Grid2 container spacing={3}>
            <Grid2 size ={12}>
              <OtpInput
                value={otp}
                onChange={(otp: string) => setOtp(otp)}
                numInputs={6}
                containerStyle={{ justifyContent: 'space-between' }}
                inputStyle={{
                  width: '100%',
                  margin: '8px',
                  padding: '10px',
                  border: `1px solid ${borderColor}`,
                  borderRadius: 4,
                  ':hover': {
                    borderColor: theme.palette.primary.main
                  }
                }}
                focusStyle={{
                  outline: 'none',
                  boxShadow: theme.customShadows.primary,
                  border: `1px solid ${theme.palette.primary.main}`
                }}
              />
            </Grid2>
            <Grid2 size ={12}>
              <AnimateButton>
                <Button
                  disableElevation
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  onClick={() => submitCodeForVerification(recoveryMode ? 'email' : undefined)}
                >
                  Continue
                </Button>
              </AnimateButton>
            </Grid2>
            {['email', 'mobileNo'].includes(authenticateWith) ? (
              <Grid2 size ={12}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography>Not received OTP?</Typography>
                  {isLoading ? (
                    <CircularProgress size={15} />
                  ) : timeLeft > 0 ? (
                    <Typography
                      variant="body1"
                      sx={{
                        minWidth: 85,
                        ml: 2,
                        textDecoration: 'none'
                      }}
                      color="primary"
                    >
                      <span style={{ color: 'black' }}>Resend in: </span> {formatTime(timeLeft)}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body1"
                      onClick={() => {
                        resetOtp();
                      }}
                      sx={{
                        minWidth: 85,
                        ml: 2,
                        textDecoration: 'none',
                        cursor: 'pointer'
                      }}
                      color="primary"
                    >
                      Resend code
                    </Typography>
                  )}
                </Stack>
              </Grid2>
            ) : (
              <Grid2 size ={12}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography>Can't get an OTP?</Typography>
                  <Typography
                    variant="body1"
                    onClick={() => {
                      setAuthenticateWith('email');
                      setRecoveryMode(true);
                      resetOtp();
                    }}
                    sx={{
                      minWidth: 85,
                      ml: 2,
                      textDecoration: 'none',
                      cursor: 'pointer'
                    }}
                    color="primary"
                  >
                    Get OTP in email
                  </Typography>
                </Stack>
              </Grid2>
            )}
          </Grid2>
        </Grid2>
      </Grid2>
    </AuthWrapper>
  );
};

export default TwoFactorAuthentication;
